<template>
  <ReportWrapper
    :loading="isGeneratingHoldReport"
    :data="holdReportResponse"
    class="h-full w-full xl:!pl-[10px] xl:!mt-0 mt-[50px]"
  >
    <template #actions>
      <CfsDateField v-model="selectedDateRange" placeholder="Select date range" range />
      <v-btn
        color="primary"
        :disabled="!selectedDateRange"
        :loading="isGeneratingHoldReport"
        @click="generate"
      >
        Generate
      </v-btn>
    </template>
    <PieChart
      ref="chart"
      :height="PIE_HEIGHT.HOLD_BY_CUSTOMER"
      current-tab="HOLD_BY_CUSTOMER"
      :average="onHoldPercent"
      :pie-data="pieChartData"
      :formatter="pieChartFormatter"
    />
  </ReportWrapper>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { useReports } from '@/composables/reports/useReports';
import PieChart from '@/components/imports/reports/charts/PieChart.vue';
import {
  PieSeriesOption,
  TooltipComponentFormatterCallback,
  TooltipComponentFormatterCallbackParams,
} from 'echarts';
import ReportWrapper from '@/components/reports/ReportWrapper.vue';

export default defineComponent({
  name: 'ReportHold',
  components: { ReportWrapper, PieChart },
  setup() {
    const chart = ref();
    const selectedDateRange = ref<{ start: string; end: string } | null>(null);

    const PIE_HEIGHT = {
      DASHBOARD: 'h-96',
      HOLD_BY_CUSTOMER: 'h-[calc(100vh-550px)]',
    };
    const { useGenerateHoldReport } = useReports();
    const {
      data: holdReportResponse,
      isLoading: isGeneratingHoldReport,
      mutateAsync: generateHoldReport,
    } = useGenerateHoldReport();

    const generate = async () => {
      await generateHoldReport(selectedDateRange.value);
    };
    const onHold = computed(() => holdReportResponse.value?.onHold ?? 0);
    const notOnHold = computed(() => holdReportResponse.value?.notOnHold ?? 0);
    const totalItems = computed(() => onHold.value + notOnHold.value);
    const onHoldPercent = computed(() =>
      Math.floor((onHold.value * 100) / (totalItems.value || 1))
    );
    const notOnHoldPercent = computed(() => (totalItems.value ? 100 - onHoldPercent.value : 0));

    const ON_HOLD_COLOR = '#6469D1';
    const NOT_ON_HOLD_COLOR = '#6ACEF9';

    const pieChartData = computed<PieSeriesOption[]>(() => [
      {
        value: onHold.value,
        name: `On Hold ${onHoldPercent.value}%`,
        itemStyle: { color: ON_HOLD_COLOR },
      },
      {
        value: notOnHold.value,
        name: `Not On Hold ${notOnHoldPercent.value}%`,
        itemStyle: { color: NOT_ON_HOLD_COLOR },
      },
    ]);

    const pieChartFormatter: TooltipComponentFormatterCallback<
      TooltipComponentFormatterCallbackParams
    > = () => {
      return `
        <div>
          <div class="waterloo--text text-xs flex items-center justify-between mb-2 gap-x-2.5">
            <div class="flex items-center gap-x-[7px]">
                <span class="block w-[10px] h-[10px] rounded-full" style="background-color: ${ON_HOLD_COLOR}"></span>
                Number of Holds:
            </div>
            <span class="text-black font-weight-bold">${onHold.value}</span>
          </div>
          <div class="waterloo--text text-xs flex items-center justify-between gap-x-2.5">
              <div class="flex items-center gap-x-[7px]">
                <span class="block w-[10px] h-[10px] rounded-full" style="background-color: ${NOT_ON_HOLD_COLOR}"></span>
                Not on Hold:
              </div>
              <span class="text-black font-weight-bold">${notOnHold.value}</span>
          </div>
        </div>`;
    };

    return {
      selectedDateRange,

      chart,
      onHoldPercent,
      pieChartData,
      PIE_HEIGHT,
      holdReportResponse,
      isGeneratingHoldReport,
      generate,
      pieChartFormatter,
    };
  },
});
</script>
